import { ArrowDownTrayIcon, CreditCardIcon, DocumentTextIcon, ExclamationTriangleIcon } from '@heroicons/react/24/outline';
import React, { useEffect, useState } from 'react';
import Skeleton from 'react-loading-skeleton';
import { useSelector } from 'react-redux';

import type { ProAbonoInvoiceDetailed, SubscriptionDetails } from '../../services/paymentApi';
import { paymentApi } from '../../services/paymentApi';
import type { RootState } from '../../store/store';
import { showToast } from '../common/Notification';

const CGV_URL = process.env.REACT_APP_CGV_URL || '/documents/CGV.pdf';

const PaymentInvoice: React.FC = () => {
  const { company, token } = useSelector((state: RootState) => state.auth);
  
  const [loading, setLoading] = useState(true);
  const [subscriptionDetails, setSubscriptionDetails] = useState<SubscriptionDetails | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!token || !company?.id) {
      return;
    }

    // After a ProAbono checkout the user is redirected here with `outcome=pending`,
    // but the invoice is only created once the payment webhook is processed. Retry a
    // few times so the newly paid subscription shows up without a manual reload.
    const isPostCheckout = new URLSearchParams(window.location.search).get('outcome') === 'pending';
    const maxAttempts = isPostCheckout ? 5 : 1;
    const retryDelayMs = 2000;

    let cancelled = false;
    let retryTimer: ReturnType<typeof setTimeout> | undefined;

    const loadSubscriptionDetails = async(attempt: number) => {
      try {
        if (attempt === 1) {
          setLoading(true);
        }
        setError(null);
        const details = await paymentApi.getSubscription(company.id, token);
        if (cancelled) {
          return;
        }
        setSubscriptionDetails(details);

        const hasInvoice = (details?.invoices?.Items?.length ?? 0) > 0;
        if (isPostCheckout && !hasInvoice && attempt < maxAttempts) {
          retryTimer = setTimeout(() => loadSubscriptionDetails(attempt + 1), retryDelayMs);
          return;
        }

        if (isPostCheckout) {
          showToast('success', 'Votre abonnement a bien été pris en compte.');
        }
      } catch (err) {
        console.error('Error loading subscription details:', err);
        if (!cancelled) {
          setError('Impossible de charger les factures. Veuillez réessayer.');
          showToast('error', 'Impossible de charger les factures');
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    loadSubscriptionDetails(1);

    return () => {
      cancelled = true;
      if (retryTimer) {
        clearTimeout(retryTimer);
      }
    };
  }, [token, company]);

  const formatPrice = (cents: number) => {
    return `${(cents / 100).toFixed(2)} € TTC`;
  };

  const formatDate = (dateString: string) => {
    try {
      return new Date(dateString).toLocaleString('fr-FR');
    } catch {
      return dateString;
    }
  };

  const getStatusBadge = (status: string, stateInvoice: string) => {
    if (status === 'Due' && stateInvoice !== 'DuePending') {
      return <span className="inline-flex items-center rounded-full bg-blue-100 px-2.5 py-0.5 text-xs font-medium text-blue-800">Émise</span>;
    } else if (status === 'Due' && stateInvoice === 'DuePending') {
      return <span className="inline-flex items-center rounded-full bg-yellow-100 px-2.5 py-0.5 text-xs font-medium text-yellow-800">Paiement en cours</span>;
    } else if (status === 'Paid') {
      return <span className="inline-flex items-center rounded-full bg-green-100 px-2.5 py-0.5 text-xs font-medium text-green-800">Payée</span>;
    } else if (status === 'Void') {
      return <span className="inline-flex items-center rounded-full bg-gray-100 px-2.5 py-0.5 text-xs font-medium text-gray-800">Annulée</span>;
    }
    return <span className="inline-flex items-center rounded-full bg-gray-100 px-2.5 py-0.5 text-xs font-medium text-gray-800">{status}</span>;
  };

  const invoices = subscriptionDetails?.invoices?.Items || [];
  const validInvoices = invoices.filter((facture: ProAbonoInvoiceDetailed) => {
      // Matches Capturama logic:
      // if facture.TypeCredit is defined and != 'Voiding' OR not defined
      // AND Status != 'Void' AND StateInvoice != 'Void'
      const isVoiding = facture.TypeCredit === 'Voiding';
      const isVoid = facture.Status === 'Void' || facture.StateInvoice === 'Void';
      return !isVoiding && !isVoid;
  });

  return (
    <div className="min-h-screen bg-gray-50 px-4 py-8 sm:px-6 lg:px-8">
      <div className="mx-auto max-w-6xl space-y-8">
        
        <div className="mb-8 text-center">
          <h1 className="mb-2 text-3xl font-bold text-gray-900">
            Facturation
          </h1>
          <p className="text-lg text-gray-600">
            Retrouvez l'historique de vos factures et de vos paiements
          </p>
        </div>

        {loading ? (
           <div className="overflow-hidden rounded-lg bg-white shadow-md">
               <div className="border-b border-gray-200 p-6">
                   <div className="flex items-center gap-2">
                       <DocumentTextIcon className="h-6 w-6 text-blue-600" />
                       <h2 className="text-xl font-semibold text-gray-900">Vos factures</h2>
                   </div>
               </div>
               <div className="space-y-4 p-6">
                   {[1, 2, 3].map((i) => (
                       <Skeleton key={i} height={60} />
                   ))}
               </div>
           </div>
        ) : error ? (
           <div className="rounded-lg bg-white p-8 shadow-lg">
            <div className="flex items-center gap-4">
              <ExclamationTriangleIcon className="h-12 w-12 text-red-500" />
              <div>
                <h2 className="text-2xl font-bold text-gray-900">Erreur</h2>
                <p className="text-gray-600">{error}</p>
              </div>
            </div>
          </div>
        ) : (
            /* Invoices List */
            <div className="overflow-hidden rounded-lg bg-white shadow-md">
                <div className="border-b border-gray-200 p-6">
                    <div className="flex items-center gap-2">
                        <DocumentTextIcon className="h-6 w-6 text-blue-600" />
                        <h2 className="text-xl font-semibold text-gray-900">Vos factures</h2>
                    </div>
                </div>

                <div className="flex items-start gap-3 border-b border-amber-200 bg-amber-50 px-6 py-4">
                    <ExclamationTriangleIcon className="mt-0.5 h-5 w-5 shrink-0 text-amber-500" />
                    <p className="text-sm text-amber-800">
                        Suite à un changement de prestataire de facturation, certaines factures émises précédemment
                        peuvent ne pas apparaître ici. Si une facture est manquante, contactez-nous à{' '}
                        <a
                            href="mailto:contact@declarations-juridiques.fr"
                            className="font-medium text-amber-900 underline hover:text-amber-950"
                        >
                            contact@declarations-juridiques.fr
                        </a>.
                    </p>
                </div>

                {validInvoices.length > 0 ? (
                    <div className="overflow-x-auto">
                        <table className="min-w-full divide-y divide-gray-200">
                            <thead className="bg-gray-50">
                                <tr>
                                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium tracking-wider text-gray-500 uppercase">
                                        Date d'émission
                                    </th>
                                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium tracking-wider text-gray-500 uppercase">
                                        Numéro
                                    </th>
                                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium tracking-wider text-gray-500 uppercase">
                                        Montant
                                    </th>
                                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium tracking-wider text-gray-500 uppercase">
                                        Statut
                                    </th>
                                    <th scope="col" className="px-6 py-3 text-right text-xs font-medium tracking-wider text-gray-500 uppercase">
                                        Actions
                                    </th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-200 bg-white">
                                {validInvoices.map((facture: ProAbonoInvoiceDetailed) => {
                                    const downloadLink = facture.Links?.find((l) => l.rel === 'hosted-invoice' || l.rel === 'hosted-invoice-pdf')?.href;
                                    const payLink = facture.Links?.find((l) => l.rel === 'hosted-payment')?.href;
                                    
                                    return (
                                        <tr key={facture.Id} className="transition-colors hover:bg-gray-50">
                                            <td className="px-6 py-4 text-sm whitespace-nowrap text-gray-900">
                                                {formatDate(facture.DateIssue)}
                                            </td>
                                            <td className="px-6 py-4 text-sm whitespace-nowrap text-gray-500">
                                                {facture.FullNumber}
                                            </td>
                                            <td className="px-6 py-4 text-sm font-medium whitespace-nowrap text-gray-900">
                                                {formatPrice(facture.AmountTotal)}
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap">
                                                {getStatusBadge(facture.Status, facture.StateInvoice)}
                                            </td>
                                            <td className="space-x-3 px-6 py-4 text-right text-sm font-medium whitespace-nowrap">
                                                {facture.Status === 'Due' && facture.StateInvoice !== 'DuePending' && payLink && (
                                                    <a
                                                        href={payLink}
                                                        className="inline-flex items-center gap-1 text-blue-600 hover:text-blue-900"
                                                    >
                                                        <CreditCardIcon className="h-4 w-4" />
                                                        Payer
                                                    </a>
                                                )}
                                                <a
                                                    href={downloadLink || facture.Links?.[0]?.href}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    className="inline-flex items-center gap-1 text-gray-600 hover:text-gray-900"
                                                >
                                                    <ArrowDownTrayIcon className="h-4 w-4" />
                                                    Télécharger
                                                </a>
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                ) : (
                    <div className="p-12 text-center">
                        <DocumentTextIcon className="mx-auto h-12 w-12 text-gray-400" />
                        <h3 className="mt-2 text-sm font-medium text-gray-900">Aucune facture</h3>
                        <p className="mt-1 text-sm text-gray-500">Vous n'avez pas encore de facture disponible.</p>
                    </div>
                )}
            </div>
        )}
        
        <div className="mt-8 border-t border-gray-200 pt-8 text-center text-sm text-gray-500">
          <p>
             Vous souhaitez vous désabonner ? Contactez-nous à{' '}
             <a href="mailto:contact@capturama.fr" className="text-blue-600 hover:underline">
               contact@capturama.fr
             </a>
             {' '}conformément aux{' '}
             <a
               href={CGV_URL}
               target="_blank"
               rel="noopener noreferrer"
               className="text-blue-600 hover:underline"
             >
               Conditions Générales de Vente
             </a>.
          </p>
        </div>
      </div>
    </div>
  );
};

export default PaymentInvoice;
